import { Box, DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import {
  BottomSheet,
  Input,
  Text,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useRef } from "react";
import { Virtuoso } from "react-virtuoso";

import { IconButton } from "../../components/IconButton";
import { ListLoadingState } from "../../components/ListLoadingState";
import { getSafeAreaPaddings } from "../../utils/getSafeAreaPaddings";
import { PlacementWorkplaceListItem } from "./Item";
import { usePlacementWorkplaceSearch } from "./usePlacementWorkplaceSearch";

interface PlacementWorkplaceSearchBottomSheetProps {
  modalState: UseModalState;
}

export function PlacementWorkplaceSearchBottomSheet(
  props: PlacementWorkplaceSearchBottomSheetProps
) {
  const { modalState } = props;
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    isLoading,
    isSuccess,
    isError,
    workplaces,
    totalCount,
    isFetchingNextPage,
    hasNextPage,
    handleLoadMore,
  } = usePlacementWorkplaceSearch();

  const isEmpty = totalCount === 0 && !isLoading;
  const logCloseEvent = () => {
    logEvent(APP_V2_USER_EVENTS.BROWSE_PLACEMENT_WORKPLACES_SEARCH_CLOSED);
  };

  return (
    <BottomSheet
      modalState={modalState}
      paperSx={{
        height: `calc(100% - ${getSafeAreaPaddings({ top: true }).paddingTop})`,
      }}
      onClose={logCloseEvent}
    >
      <DialogContent
        sx={{
          gap: 5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack sx={{ position: "relative", pt: 3 }}>
          <Text semibold variant="h5" align="center">
            Find a workplace
          </Text>
          <IconButton
            size="small"
            variant="outlined"
            iconType="close"
            sx={(theme) => ({
              boxShadow: theme.shadows[2],
              outlineColor: theme.palette.border?.subtle,
              position: "absolute",
              right: 0,
              top: 0,
            })}
            onClick={() => {
              logCloseEvent();
              modalState.closeModal();
            }}
          />
        </Stack>

        <Input
          placeholder="Search for a workplace"
          iconType="search"
          value={searchTerm}
          onChange={(event) => {
            setSearchTerm(event.target.value);
          }}
        />

        <Stack
          ref={scrollRef}
          sx={(theme) => ({
            height: "100%",
            minHeight: theme.size?.bottomSheet.loading.minHeight,
            justifyContent: isLoading ? "center" : "flex-start",
            overflow: "auto",
          })}
        >
          {isLoading && (
            <ListLoadingState
              texts={[
                "Finding workplaces nearby",
                "Workplaces are on the way",
                "Searching for the best workplaces nearby",
              ]}
            />
          )}

          {!isLoading && isError && (
            <Text align="center">Error loading workplaces. Please try again.</Text>
          )}

          {!isLoading && isSuccess && isEmpty && (
            <Text semibold align="center" variant="h6" sx={{ pt: 5 }}>
              {debouncedSearchTerm
                ? `No workplaces found for "${debouncedSearchTerm}"`
                : "No workplaces found nearby"}
            </Text>
          )}

          {!isLoading && workplaces.length > 0 && (
            <>
              <Stack direction="row" gap={2} sx={{ pb: 2 }}>
                <Text variant="body1" color={(theme) => theme.palette.text.tertiary}>
                  All workplaces
                </Text>
                <Text variant="body1" color={(theme) => theme.palette.text.secondary}>
                  {totalCount}
                </Text>
              </Stack>
              <Virtuoso
                data={workplaces}
                customScrollParent={scrollRef.current ?? undefined}
                endReached={() => {
                  if (hasNextPage && !isFetchingNextPage) {
                    void handleLoadMore();
                  }
                }}
                itemContent={(index, workplace) => (
                  <Box pt={index === 0 ? 0 : 5}>
                    <PlacementWorkplaceListItem
                      workplaceId={workplace.workplaceId}
                      name={workplace.name}
                      formattedDistance={workplace.formattedDistance}
                      placementsCount={workplace.placementsCount}
                      searchTerm={debouncedSearchTerm}
                    />
                  </Box>
                )}
              />
            </>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
